// Every rendered date and time, in the display format of lib/displayFormat, in the zone the
// caller passes.

import type { NonEmpty } from '@/types/common';
import type { DisplayFormat } from '@/types/displayFormat';
import { DateOrder } from '@/types/displayFormat';

import { DISPLAY_FORMAT } from './displayFormat';
import {
    DAY_H, DAY_MS, HOUR_MIN, HOUR_MS, MINUTE_MS, MINUTE_S, MONTH_MS, SECOND_MS, WEEK_MS,
} from './timeUnits';

const abbrFmtCache = new Map<string, Intl.DateTimeFormat>();
function getAbbrFormatter(zone: string): Intl.DateTimeFormat {
    let formatter = abbrFmtCache.get(zone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' });
        abbrFmtCache.set(zone, formatter);
    }
    return formatter;
}

/** Short zone label ("PST"/"PDT") for `zone` at the given instant. */
export function fmtZoneAbbr(ms: number, zone: string): string {
    const parts = getAbbrFormatter(zone).formatToParts(ms);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
}

// Suffix policy: timestamps carry the zone abbr exactly when `zone` differs from
// the viewer's own — an LA viewer sees nothing extra, a Bangkok viewer sees
// "PDT". Dense contexts (timeline tick labels) opt out and show the zone once in
// their own chrome instead.
function getZoneSuffix(ms: number, zone: string): string {
    return doesZoneDifferFromViewer(zone) ? ` ${fmtZoneAbbr(ms, zone)}` : '';
}

interface TimeOpts {
    hasSuffix?: boolean;
    showsSeconds?: boolean;
    /** False drops AM/PM: a tick under its labelled hour, or a range that states it once. */
    period?: boolean;
    format?: DisplayFormat;
}

interface DateOpts {
    showsYear?: boolean;
    showsWeekday?: boolean;
    /** "March 16" in place of "Mar 16". */
    isLong?: boolean;
    format?: DisplayFormat;
}

interface DateTimeOpts extends Omit<TimeOpts, 'period'> {
    showsYear?: boolean;
}

interface MonthOpts {
    showsYear?: boolean;
    format?: DisplayFormat;
}

interface SpanOpts {
    showsSeconds?: boolean;
    format?: DisplayFormat;
}

interface RangeOpts {
    isLive?: boolean;
    format?: DisplayFormat;
}

/** The text of each field of one instant, as en-US writes it. */
interface Fields {
    weekday: string;
    year: string;
    month: string;
    day: string;
    hour: string;
    minute: string;
    second: string;
    dayPeriod: string;
}

// en-US names in our own order: en-GB writes a 24-hour "9:05" and, in newer ICU, "Sept".
const fieldsFmtCache = new Map<string, Intl.DateTimeFormat>();
function getFieldsFormatter(zone: string, isHour24: boolean,
    isLong: boolean): Intl.DateTimeFormat {
    const key = `${zone}|${isHour24}|${isLong}`;
    let formatter = fieldsFmtCache.get(key);
    if (!formatter) {
        const width = isLong ? 'long' : 'short';
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: zone,
            hourCycle: isHour24 ? 'h23' : 'h12',
            weekday: width,
            year: 'numeric',
            month: width,
            day: 'numeric',
            hour: isHour24 ? '2-digit' : 'numeric',
            minute: '2-digit',
            second: '2-digit',
        });
        fieldsFmtCache.set(key, formatter);
    }
    return formatter;
}

function getFields(ms: number, zone: string, isHour24: boolean, isLong = false): Fields {
    const parts: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {};
    for (const { type, value } of getFieldsFormatter(zone, isHour24, isLong).formatToParts(ms)) {
        parts[type] = value;
    }
    const hour = parts.hour === '24' ? '00' : parts.hour; //< h23 edge in some ICU versions.
    return {
        weekday: parts.weekday ?? '',
        year: parts.year ?? '',
        month: parts.month ?? '',
        day: parts.day ?? '',
        hour: hour ?? '',
        minute: parts.minute ?? '',
        second: parts.second ?? '',
        dayPeriod: parts.dayPeriod ?? '',
    };
}

function getClockText(
    fields: Fields, isHour24: boolean, { showsSeconds = false,
        period: showsPeriod = true }: TimeOpts,
): string {
    const minutes = `${fields.hour}:${fields.minute}`;
    const clock = showsSeconds ? `${minutes}:${fields.second}` : minutes;
    return isHour24 || !showsPeriod ? clock : `${clock} ${fields.dayPeriod}`;
}

function getYearDateText(fields: Fields, dateOrder: DateOrder): string {
    switch (dateOrder) {
        case DateOrder.MonthFirst:
            return `${fields.month} ${fields.day}, ${fields.year}`;
        case DateOrder.DayFirst:
            return `${fields.day} ${fields.month} ${fields.year}`;
        case DateOrder.YearFirst:
            return `${fields.year} ${fields.month} ${fields.day}`;
    }
}

// Without its year, a year-first date reads day-first: the year is what leads it.
function getDayDateText(fields: Fields, dateOrder: DateOrder): string {
    switch (dateOrder) {
        case DateOrder.MonthFirst:
            return `${fields.month} ${fields.day}`;
        case DateOrder.DayFirst:
        case DateOrder.YearFirst:
            return `${fields.day} ${fields.month}`;
    }
}

function getDateText(
    fields: Fields, dateOrder: DateOrder, { showsYear = false, showsWeekday = false }: DateOpts,
): string {
    const date = showsYear ? getYearDateText(fields, dateOrder) : getDayDateText(fields,
        dateOrder);
    return showsWeekday ? `${fields.weekday}, ${date}` : date;
}

/** Clock time, "4:02 PM" or "16:02", in `zone`. */
export function fmtTime(ms: number, zone: string, opts: TimeOpts = {}): string {
    const { hasSuffix = true, format = DISPLAY_FORMAT } = opts;
    const text = getClockText(getFields(ms, zone, format.isHour24), format.isHour24, opts);
    return hasSuffix ? text + getZoneSuffix(ms, zone) : text;
}

/** Date, "Jun 5", "5 Jun" or with its year "2026 Jun 5", in `zone`. */
export function fmtDate(ms: number, zone: string, opts: DateOpts = {}): string {
    const { isLong = false, format = DISPLAY_FORMAT } = opts;
    return getDateText(getFields(ms, zone, format.isHour24, isLong), format.dateOrder, opts);
}

/** Weekday, "Monday", in `zone`. */
export function fmtWeekday(ms: number, zone: string): string {
    return getFields(ms, zone, false, true).weekday;
}

/** The day of the month alone, "16", in `zone`: a cell under a row that names the month. */
export function fmtDayOfMonth(ms: number, zone: string): string {
    return getFields(ms, zone, false).day;
}

/** The year alone, "2026", in `zone`. */
export function fmtYear(ms: number, zone: string): string {
    return getFields(ms, zone, false).year;
}

/** Month, "Jun", or with its year "Jun 2026" or "2026 Jun", in `zone`. */
export function fmtMonth(
    ms: number, zone: string, { showsYear = false, format = DISPLAY_FORMAT }: MonthOpts = {},
): string {
    const fields = getFields(ms, zone, format.isHour24);
    if (!showsYear) {
        return fields.month;
    }
    switch (format.dateOrder) {
        case DateOrder.YearFirst:
            return `${fields.year} ${fields.month}`;
        case DateOrder.MonthFirst:
        case DateOrder.DayFirst:
            return `${fields.month} ${fields.year}`;
    }
}

/** Date + clock time, "Jun 5, 4:02 PM" or "5 Jun, 16:02", in `zone`. The default for
 *  full-frame preview overlays (matches the Figma preview timestamp).
 *  `year: true` adds the year — for dates old enough that the day alone
 *  misleads (e.g. enrollment dates). */
export function fmtDateTime(ms: number, zone: string, opts: DateTimeOpts = {}): string {
    const { hasSuffix = true, showsYear = false, format = DISPLAY_FORMAT } = opts;
    const fields = getFields(ms, zone, format.isHour24);
    const date = getDateText(fields, format.dateOrder, { showsYear });
    const text = `${date}, ${getClockText(fields, format.isHour24, opts)}`;
    return hasSuffix ? text + getZoneSuffix(ms, zone) : text;
}

/** Clock time for an event from today (in `zone`), else date + clock time:
 *  "10:01" today vs "Jun 14, 10:01" on an earlier day, in the display format. Lets a dense
 *  overlay (the object grid) stay compact for recent shots while disambiguating older
 *  ones. "Today" is judged in the same `zone`, so an event and `now` are
 *  compared on the one clock. */
export function fmtTimeOrDateTime(ms: number, zone: string, opts?: DateTimeOpts): string {
    return getDayKey(ms, zone) === getDayKey(Date.now(), zone)
        ? fmtTime(ms, zone, opts)
        : fmtDateTime(ms, zone, opts);
}

/** The heading of the calendar day of `ms` in `zone`: "Today", "Yesterday", or
 *  "Thursday, October 22", with the year when it is not the current year of `zone`. */
export function fmtDayHeading(ms: number, zone: string, format = DISPLAY_FORMAT): string {
    const dayKey = getDayKey(ms, zone);
    const nowMs = Date.now();
    if (dayKey === getDayKey(nowMs, zone)) {
        return 'Today';
    }
    // The calendar day before, not 24 h before: a day with a DST change has 23 or 25 hours.
    const today = getPartsInZone(nowMs, zone);
    if (dayKey === getDayKey(Date.UTC(today.year, today.month - 1, today.day - 1), 'UTC')) {
        return 'Yesterday';
    }
    const showsYear = getPartsInZone(ms, zone).year !== today.year;
    return fmtDate(ms, zone, { showsWeekday: true, isLong: true, showsYear, format });
}

const HALF_DAY_H = 12;

function getClockPeriod(ms: number, zone: string, format: DisplayFormat): 'AM' | 'PM' | null {
    if (format.isHour24) {
        return null;
    }
    return getPartsInZone(ms, zone).hour >= HALF_DAY_H ? 'PM' : 'AM';
}

/** Two clock times of one day: a shared AM/PM once, after the end. "4:00 – 5:30 PM",
 *  "11:50 AM – 12:10 PM", "16:00 – 17:30". */
export function fmtClockSpan(
    startMs: number, endMs: number, zone: string,
    { showsSeconds = false, format = DISPLAY_FORMAT }: SpanOpts = {},
): string {
    const showsPeriod = getClockPeriod(startMs, zone, format) !== getClockPeriod(endMs, zone,
        format);
    const start = fmtTime(startMs, zone, {
        hasSuffix: false,
        showsSeconds,
        period: showsPeriod,
        format,
    });
    const end = fmtTime(endMs, zone, {
        hasSuffix: false,
        showsSeconds,
        format,
    });
    return `${start} – ${end}`;
}

/** Compact elapsed-duration label for a span in ms, e.g. "12s", "1m 30s",
 *  "2m". Used for the merged-moment duration shown after a preview's
 *  timestamp. Whole-minute spans drop the seconds. */
export function fmtDuration(ms: number): string {
    const totalSec = Math.max(0, Math.round(ms / SECOND_MS));
    if (totalSec < MINUTE_S) { return `${totalSec}s`; }
    const minutes = Math.floor(totalSec / MINUTE_S);
    const seconds = totalSec % MINUTE_S;
    return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

/** A duration as a reader counts it: milliseconds under a second, seconds under a minute,
 *  else minutes, else hours, else days; "350 ms", "4 s", "2 min", "1 h 30 min", "2 d 5 h".
 *  For a span read at a glance, where fmtDuration's second-exact label is noise. */
export function fmtDurationTwoUnits(ms: number): string {
    if (ms < SECOND_MS) { return `${Math.round(ms)} ms`; }
    const seconds = Math.round(ms / SECOND_MS);
    if (seconds < MINUTE_S) { return `${seconds} s`; }
    const minutes = Math.round(seconds / MINUTE_S);
    if (minutes < HOUR_MIN) { return `${minutes} min`; }
    const hours = Math.floor(minutes / HOUR_MIN);
    if (hours < DAY_H) {
        const rest = minutes % HOUR_MIN;
        return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
    }
    const days = Math.floor(hours / DAY_H);
    const rest = hours % DAY_H;
    return rest === 0 ? `${days} d` : `${days} d ${rest} h`;
}

/** fmtDurationTwoUnits in the compact form of fmtDuration: "4s", "2m", "1h 30m", "24h", "7d".
 *  Hours count up to two days, so a window of a day reads "24h". */
export function fmtDurationCompact(ms: number): string {
    const seconds = Math.max(0, Math.round(ms / SECOND_MS));
    if (seconds < MINUTE_S) { return `${seconds}s`; }
    const minutes = Math.round(seconds / MINUTE_S);
    if (minutes < HOUR_MIN) { return `${minutes}m`; }
    const hours = Math.floor(minutes / HOUR_MIN);
    if (hours < 2 * DAY_H) {
        const rest = minutes % HOUR_MIN;
        return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
    }
    const days = Math.floor(hours / DAY_H);
    const rest = hours % DAY_H;
    return rest === 0 ? `${days}d` : `${days}d ${rest}h`;
}

/** A unit of fmtDurationRounded, with its word for one and for many. */
interface RoundedUnit {
    unitMs: number;
    one: string;
    many: string;
}

const ROUNDED_UNITS: NonEmpty<RoundedUnit> = [
    { unitMs: SECOND_MS, one: 'sec', many: 'sec' },
    { unitMs: MINUTE_MS, one: 'min', many: 'min' },
    { unitMs: HOUR_MS, one: 'hr', many: 'hr' },
    { unitMs: DAY_MS, one: 'day', many: 'days' },
    { unitMs: WEEK_MS, one: 'week', many: 'weeks' },
    { unitMs: MONTH_MS, one: 'month', many: 'months' },
];

/** A duration rounded in its largest unit: "40 sec", "4 hr", "1 day", "3 weeks", "2 months".
 *  A count that rounds to a whole next unit takes that unit. */
export function fmtDurationRounded(ms: number): string {
    let unit: RoundedUnit = ROUNDED_UNITS[0];
    for (const nextRoundedUnit of ROUNDED_UNITS.slice(1)) {
        if (Math.round(ms / unit.unitMs) <
            Math.floor(nextRoundedUnit.unitMs / unit.unitMs)) { break; }
        unit = nextRoundedUnit;
    }
    const count = Math.max(1, Math.round(ms / unit.unitMs));
    return `${count} ${count === 1 ? unit.one : unit.many}`;
}

/** A zone's offset from UTC as a clock reads it: "UTC+02:00", "UTC-07:00". */
export function fmtUtcOffset(minutes: number): string {
    const sign = minutes < 0 ? '-' : '+';
    const abs = Math.abs(minutes);
    const hh = String(Math.floor(abs / HOUR_MIN)).padStart(2, '0');
    const mm = String(abs % HOUR_MIN).padStart(2, '0');
    return `UTC${sign}${hh}:${mm}`;
}

/** A span in `zone`, the date once unless the span crosses midnight:
 *  "Jun 5, 4:02 – 4:30 PM", "Jun 5, 11:50 PM – Jun 6, 12:10 AM". The zone suffix, when
 *  the policy above gives one, follows the end once and names the whole span. */
export function fmtSpan(
    startMs: number, endMs: number, zone: string, format: DisplayFormat = DISPLAY_FORMAT,
): string {
    const dateTimeOptions = {
        hasSuffix: false,
        format,
    };
    if (getDayKey(startMs, zone) === getDayKey(endMs, zone)) {
        const clocks = fmtClockSpan(startMs, endMs, zone, { format });
        return `${fmtDate(startMs, zone, { format })}, ${clocks}${getZoneSuffix(endMs, zone)}`;
    }
    const span = `${fmtDateTime(startMs, zone, dateTimeOptions)} – ${
        fmtDateTime(endMs, zone, dateTimeOptions)}`;
    return span + getZoneSuffix(endMs, zone);
}

// ─── Display zone ───────────────────────────────────────────────────────────
// Every timestamp renders in the zone of the server (location) its item belongs
// to — the caller resolves that zone (see hooks/useZoneResolver) and passes it
// in. Timestamps with no camera use the viewer's own browser zone. There is no
// global display zone: a single mutable zone leaked one camera's time into
// unrelated panels and never reverted.

/** The viewer's own IANA zone, from the browser. The zone to pass for a
 *  timestamp that belongs to no camera (account metadata, sign-ins, uploads). */
export function getViewerZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** True when `zone` differs from the viewer's browser zone — the condition for
 *  appending a zone suffix (e.g. "PST") to a rendered timestamp. */
export function doesZoneDifferFromViewer(zone: string): boolean {
    return zone !== getViewerZone();
}

// ─── Zone-aware calendar primitives ─────────────────────────────────────────

const partsFmtCache = new Map<string, Intl.DateTimeFormat>();
function getPartsFormatter(zone: string): Intl.DateTimeFormat {
    let formatter = partsFmtCache.get(zone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: zone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        partsFmtCache.set(zone, formatter);
    }
    return formatter;
}

/** Wall-clock fields of an instant in one zone; month is 1-12 and hour 0-23. */
interface WallClock {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
}

/** A wall-clock reading with the time-of-day fields defaulting to midnight. */
type WallClockInput =
  Pick<WallClock, 'year' | 'month' | 'day'> & Partial<Omit<WallClock, 'year' | 'month' | 'day'>>;

/** Wall-clock fields of the instant `ms` in `zone`:
 *  { year, month (1-12), day, hour (0-23), minute, second }. */
export function getPartsInZone(ms: number, zone: string): WallClock {
    const outRecord: Record<string, number> = {};
    for (const { type, value } of getPartsFormatter(zone).formatToParts(ms)) {
        if (type !== 'literal') { outRecord[type] = Number(value); }
    }
    if (outRecord.hour === DAY_H) {
        outRecord.hour = 0; //< h23 edge in some ICU versions.
    }
    return {
        year: outRecord.year ?? 0, month: outRecord.month ?? 0, day: outRecord.day ?? 0,
        hour: outRecord.hour ?? 0, minute: outRecord.minute ?? 0, second: outRecord.second ?? 0,
    };
}

/** Inverse of getPartsInZone: the UTC epoch ms at which `zone`'s wall clock reads
 *  the given fields. Standard Intl round-trip: guess, measure, correct — two
 *  rounds converge, including across DST edges. A nonexistent wall time
 *  (spring-forward gap) resolves to a real neighboring instant. */
export function convertWallClockToUtc(
    { year, month, day, hour = 0, minute = 0, second = 0 }: WallClockInput,
    zone: string): number {
    const targetMs = Date.UTC(year, month - 1, day, hour, minute, second);
    let ts = targetMs;
    for (let i = 0; i < 2; i++) {
        const p = getPartsInZone(ts, zone);
        ts += targetMs - Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    }
    return ts;
}

const YEAR_DIGITS = 4;
const MS_DIGITS = 3;

/** A calendar field on a two-digit clock: 9 reads as "09". */
export const pad2 = (value: number): string => String(value).padStart(2, '0');

/** ISO-8601 of an instant in `zone`'s own offset: "2026-09-17T14:36:25.583-07:00". The
 *  fraction appears only when non-zero, trailing zeros trimmed, and a zero offset is "Z" —
 *  the Go services' layout, so a time reads the same whichever side wrote it. */
export function fmtIsoInZone(ms: number, zone: string): string {
    const at = Math.round(ms);
    const wholeMs = Math.floor(at / SECOND_MS) * SECOND_MS;
    const wall = getPartsInZone(wholeMs, zone);
    const wallMs = Date.UTC(
        wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
    const offsetMin = Math.round((wallMs - wholeMs) / MINUTE_MS);
    const fraction = at === wholeMs
        ? ''
        : `.${String(at - wholeMs).padStart(MS_DIGITS, '0').replace(/0+$/, '')}`;
    const abs = Math.abs(offsetMin);
    const sign = offsetMin < 0 ? '-' : '+';
    const offset = offsetMin === 0
        ? 'Z'
        : `${sign}${pad2(Math.floor(abs / HOUR_MIN))}:${pad2(abs % HOUR_MIN)}`;
    const date = `${String(wall.year).padStart(YEAR_DIGITS, '0')}-${pad2(wall.month)}-` +
        pad2(wall.day);
    const clock = `${pad2(wall.hour)}:${pad2(wall.minute)}:${pad2(wall.second)}`;
    return `${date}T${clock}${fraction}${offset}`;
}

const ISO_WITH_OFFSET = new RegExp(
    '^(?<year>\\d{4})-(?<month>\\d{2})-(?<day>\\d{2})T(?<hour>\\d{2}):(?<minute>\\d{2})' +
    '(?::(?<second>\\d{2})(?:\\.(?<fraction>\\d+))?)?' +
    '(?<offset>Z|(?<sign>[+-])(?<offsetHour>\\d{2}):(?<offsetMinute>\\d{2}))$', 'i');

/** Epoch ms of an ISO-8601 time that names its UTC offset ("Z" or "±hh:mm"), else null. A time
 *  without one is refused rather than read in some zone: its writer left the zone unsaid. */
export function parseIsoWithOffset(text: string): number | null {
    const fields = ISO_WITH_OFFSET.exec(text.trim())?.groups;
    if (!fields) { return null; }
    const year = Number(fields.year);
    const month = Number(fields.month);
    const day = Number(fields.day);
    const hour = Number(fields.hour);
    const minute = Number(fields.minute);
    const second = Number(fields.second ?? '0');
    const offsetHour = Number(fields.offsetHour ?? '0');
    const offsetMinute = Number(fields.offsetMinute ?? '0');
    if (offsetHour >= DAY_H || offsetMinute >= HOUR_MIN) { return null; }
    const wall = Date.UTC(year, month - 1, day, hour, minute, second);
    // Date.UTC rolls an out-of-range field over (Feb 30 → Mar 2); a real time survives the trip.
    const backDate = new Date(wall);
    if (backDate.getUTCFullYear() !== year || backDate.getUTCMonth() !== month - 1 ||
        backDate.getUTCDate() !== day || backDate.getUTCHours() !== hour ||
        backDate.getUTCMinutes() !== minute || backDate.getUTCSeconds() !== second) {
        return null;
    }
    const offsetMin = (fields.offset ?? '').toUpperCase() === 'Z'
        ? 0
        : (fields.sign === '-' ? -1 : 1) * (offsetHour * HOUR_MIN + offsetMinute);
    const fractionMs = fields.fraction
        ? Math.round(Number(`0.${fields.fraction}`) * SECOND_MS)
        : 0;
    return wall - offsetMin * MINUTE_MS + fractionMs;
}

/** A `datetime-local` value is the start of an ISO time, to the minute. */
const DATE_TIME_INPUT_LENGTH = 'YYYY-MM-DDTHH:mm'.length;

/** The value of a `datetime-local` input: the minute of `ms` on the wall clock of `zone`. */
export function fmtDateTimeInput(ms: number, zone: string): string {
    const minute = Math.floor(ms / MINUTE_MS) * MINUTE_MS;
    return fmtIsoInZone(minute, zone).slice(0, DATE_TIME_INPUT_LENGTH);
}

/**
 * The instant of a `datetime-local` input value on the wall clock of `zone`, or null while the
 * input is empty. The input gives only an empty value or a valid date and time, so the text
 * parses as a date: read as UTC, its fields are the wall clock.
 */
export function parseDateTimeInput(text: string, zone: string): number | null {
    const wall = new Date(`${text}Z`);
    if (Number.isNaN(wall.getTime())) { return null; }
    return convertWallClockToUtc({
        year: wall.getUTCFullYear(),
        month: wall.getUTCMonth() + 1,
        day: wall.getUTCDate(),
        hour: wall.getUTCHours(),
        minute: wall.getUTCMinutes(),
        second: wall.getUTCSeconds(),
    }, zone);
}

const TIME_OF_DAY = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/** The minute of the day of a 24-hour time ("2:00", "23:05"), or null for any other text. */
export function parseTimeOfDay(text: string): number | null {
    const fields = TIME_OF_DAY.exec(text);
    if (!fields) { return null; }
    return Number(fields[1]) * HOUR_MIN + Number(fields[2]);
}

/** The "HH:MM" of a minute of the day. */
export function fmtTimeOfDay(minute: number): string {
    return `${pad2(Math.floor(minute / HOUR_MIN))}:${pad2(minute % HOUR_MIN)}`;
}

/** Calendar-day identity of an instant in `zone` ("2026-01-16").
 *  Use this instead of toDateString() for any same-day comparison. */
export function getDayKey(ms: number, zone: string): string {
    const parts = getPartsInZone(ms, zone);
    return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

/** Precise label for a [startMs, endMs] window — used by the time-range filter
 *  pill when it mirrors the camera_view timeline. Unlike a coarse date range,
 *  this always carries the date AND clock time of each edge so the pill reflects
 *  the EXACT visible window:
 *    - cross-day:  "Jun 11, 12:00 PM – Jun 12, 10:00 PM"
 *    - same-day:   "Jun 11, 12:00 – 10:00 PM"      (end date dropped as redundant)
 *    - live end:   "Jun 11, 12:00 PM – now"        (when `live`, or `endMs` is null)
 *  Returns '' for an unset start. The em-dash separator is a literal so callers
 *  can split on it locale-independently. */
export function fmtRangeLabelExact(
    startMs: number | null,
    endMs: number | null,
    zone: string,
    { isLive = false, format = DISPLAY_FORMAT }: RangeOpts = {},
): string {
    if (startMs === null) {
        return '';
    }
    if (isLive || endMs === null) {
        const since = fmtDateTime(startMs, zone, {
            hasSuffix: false,
            format,
        });
        return `${since} – now${getZoneSuffix(endMs ?? startMs, zone)}`;
    }
    return fmtSpan(startMs, endMs, zone, format);
}
